import React from 'react';
import { Typography, Box, Stack } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IProject, IUserSummary, UserSummary } from '../../types/global/helper';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { convertMinuteSecondsDays, createFullName, formatNumberWithK } from '../../utils/AppUtils';
import { IEntries } from '../../redux/contest/types';
import { ProjectPlayer } from './ProjectPlayer';

export interface IContestSubmissionCard extends IEntries {
    onPlay?: (project: IProject) => void;
}

const ContestSubmissionCard: React.FC<IContestSubmissionCard> = ({
    project,
    user,
    total_votes,
    awards,
    position,
    onPlay,
}) => {
    const { themeStyle } = useThemeContext();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: 'fit-content',
                mb: '20px',
                cursor: 'pointer',
                position: 'relative',
            }}
            key={project._id}>
            <Box sx={{ gap: '15px', width: '100%' }}>
                <ProjectPlayer
                    project={project}
                    total_votes={total_votes}
                    awards={awards}
                    position={position}
                    onPlay={onPlay}
                />

                <Stack
                    direction="row"
                    alignItems="flex-start"
                    mt={1.5}
                    px={{
                        xs: 2,
                        sm: 0,
                    }}>
                    <ThspianAvatar
                        user={user as unknown as IUserSummary}
                        badgeSx={{
                            border: `1px solid ${theme[themeStyle].tag_white}`,
                            borderRadius: '50%',
                        }}
                    />
                    <Box sx={{ gap: '2px', marginLeft: 1 }}>
                        <Typography
                            sx={{
                                fontSize: 14,
                                fontWeight: 'bold',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textAlign: 'left',
                                color: theme[themeStyle].filters_white,
                                textTransform: 'capitalize',
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                            }}>
                            {project.title}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: '500',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                textAlign: 'left',
                                color: theme[themeStyle].background_gray,
                                textTransform: 'capitalize',
                            }}>
                            {createFullName(user as unknown as UserSummary)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: '500',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    textAlign: 'left',
                                    color: theme[themeStyle].background_gray,
                                }}>
                                {formatNumberWithK(project.view_count!)} View
                                {`${project.view_count && project.view_count > 1 ? 's' : ''}`}
                            </Typography>
                            <hr
                                style={{
                                    width: '4px',
                                    height: '4px',
                                    borderRadius: 100,
                                    marginRight: '4px',
                                    marginLeft: '4px',
                                    backgroundColor: theme[themeStyle].form_label,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: '500',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    textAlign: 'left',
                                    color: theme[themeStyle].background_gray,
                                }}>
                                {convertMinuteSecondsDays(project.createdAt)} ago
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};

export { ContestSubmissionCard };
